import {
  BaseGraphAttribute,
  BaseGraphMetaZipGroup,
  BaseGraphMetaZipNode,
  BaseGraphTableNode,
  BaseGraphZipNode,
  BaseGraphZipNodeProperties,
} from "@store/services/converter/types"
import { Edge, InternalNode, Node as NodeFlow } from "@xyflow/react"

type Node<DataType extends NodeDataType, Type extends NodeType> = NodeFlow<DataType, NodeType> & {
  type: Type
}

export type NodeProperties = BaseGraphZipNodeProperties & {
  parents_lst: NonNullable<BaseGraphZipNodeProperties["parents_lst"]>
  a_sample_value: NonNullable<BaseGraphZipNodeProperties["a_sample_value"]> | null
}

type BaseNode<TType extends NodeType> = Node<
  NodeDataType<{
    metaNode: BaseGraphMetaZipNode
    originalNode: BaseGraphZipNode<NodeProperties>
  }>,
  TType
>

export type EmptyNode = BaseNode<NodeType.Empty>
export type CategoricalNode = BaseNode<NodeType.Categorical>
export type DeterministicNode = BaseNode<NodeType.Deterministic>
export type DiscreteNode = BaseNode<NodeType.Discrete>
export type ForecastNode = BaseNode<NodeType.Forecast>
export type LagNode = BaseNode<NodeType.Lag>
export type MixtureNode = BaseNode<NodeType.Mixture>
export type ScenarioNode = BaseNode<NodeType.Scenario>

export type ContinuousNode = Node<
  NodeDataType<{
    metaNode: BaseGraphMetaZipNode
    originalNode: BaseGraphZipNode<NodeProperties>
    expression?: string
  }>,
  NodeType.Continuous
>
export type TableNode = Node<
  NodeDataType<{
    metaNode: BaseGraphMetaZipNode
    originalAttribute: BaseGraphAttribute<TableNodeProperties>
  }>,
  NodeType.Table
>

export type ScenarioTableNode = Node<
  NodeDataType<{
    metaNode: BaseGraphMetaZipNode
    originalAttribute: BaseGraphAttribute<TableNodeProperties>
  }>,
  NodeType.ScenarioTable
>

export type ForecastTableNode = Node<
  NodeDataType<{
    metaNode: BaseGraphMetaZipNode
    originalAttribute: BaseGraphAttribute<TableNodeProperties>
  }>,
  NodeType.ForecastTable
>

export type GroupNode = Node<
  NodeDataType<{
    metaGroup: BaseGraphMetaZipGroup
  }>,
  NodeType.Group
>

export type AppNode =
  | EmptyNode
  | CategoricalNode
  | ContinuousNode
  | DeterministicNode
  | DiscreteNode
  | ForecastNode
  | LagNode
  | MixtureNode
  | ScenarioNode
  | TableNode
  | GroupNode
  | ScenarioTableNode
  | ForecastTableNode

export type NodeDataType<T extends Record<string, unknown> = {}> = {
  label?: string
  nestingLevel: number
  comments: string
} & T

export type EdgeData = {
  finalSource: string
  finalTarget: string
}

// parents_lst is something we're adding from our side and will be ignored by tkrisk (only for table nodes)
export type TableNodeProperties = BaseGraphTableNode & { parents_lst?: string[] }

// it looks dummy, but thanks to that we can narrow down unioned node type
export const isNotGroupNode = (node: AppNode | InternalNode): node is Exclude<AppNode, GroupNode> =>
  node.type !== NodeType.Group

export const isNotTableNode = (
  node: AppNode,
): node is Exclude<AppNode, TableNode | ForecastTableNode | ScenarioTableNode> =>
  node.type !== NodeType.Table &&
  node.type !== NodeType.ForecastTable &&
  node.type !== NodeType.ScenarioTable

export enum NodeType {
  Empty = "empty",
  Group = "group",
  Categorical = "categorical",
  Continuous = "continuous",
  Deterministic = "deterministic",
  Discrete = "discrete",
  Forecast = "forecast",
  Lag = "lag",
  Mixture = "mixture",
  Table = "table",
  Scenario = "scenario",
  ScenarioTable = "scenarioTable",
  ForecastTable = "forecastTable",
}

export type NodesMap = { [key: string]: AppNode }
export type EdgesMap = { [key: string]: Edge<EdgeData>[] }
